import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    get as _get,
    set as _set,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import UnscheduleIcon from '@mui/icons-material/CancelScheduleSend';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';

import Push from '../../../components/push/';
import DateTimePicker from '../../../components/styled/datetimepicker';
import ConfirmationDialog from '../../../components/confirmationdlg';

import UpgradeService from '../../../data/upgrade-service';
import PolicyService from '../../../data/policy-service';
import BacktestingService from '../../../data/backtesting-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';

import { purple, normal } from '../../../data/colors';
import Auth, { ProfileContext } from '../../../data/auth-service';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    progressLabel: {
        position: 'relative',
        zIndex: '10',
        top: '38px',
        width: '100%',
        marginTop: '-50px',
        textAlign: 'center',
    },
    progressContainer: {
        width: '100%',
    },
    progressHeight: {
        height: '30px',
        borderRadius: '3px',
        marginTop: '30px',
    },
    progressBar: {
        height: '25px',
        borderRadius: '3px',
    },
    colorPrimary: {
        background: purple[400],
        height: '30px',
        borderRadius: '3px',
    },
    statusMessage: {
        position: 'relative',
        top: '55px',
        zIndex: 10,
        marginTop: '-50px',
        textAlign: 'center'
    },
    colorPrimaryError: {
        background: theme.palette.error.main,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryDone: {
        background: normal,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryUploading: {
        background: purple[200],
        height: '30px',
        borderRadius: '3px',
    },
    tableHeaderActions: {
        border: `1px solid ${theme.palette.grey.A700}`,
        borderBottom: 'none',
    },
    popoverContainer: {
        background: theme.palette.common.black,
        border: `2px solid ${theme.palette.background.paper}`,
        bordeRadius: '3px',
    },
    backdrop: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 10000,
    },
    infoPanel: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.info.main}`,
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(2),
    },
    hideBox: {
        height: '0px',
        width: '0px',
        overflow: 'hidden',
    },
}));

const UPGRADE_INFORMATION = `Sensors and backtesting sensors (btsensor) can be assigned the same policy. A sensor or btsensor
will only be upgraded if an install_sensor or install_btsensor package has been uploaded respectively.
If one of the packages is not available an upgrade will not be deployed to that appliance.`;

const SensorUpgrade = () => {
    const classes = useStyles();
    const theme = useTheme();
    const params = useParams();
    const [policy, setPolicy] = useState();
    const [preStage, setPreStage] = useState([]);
    const [upgrades, setUpgrades] = useState([]);
    const [staged, setStaged] = useState([]);
    const [deployed, setDeployed] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [btSchedule, setBTSchedule] = useState([]);
    const [uploadFile, setUploadFile] = useState({ name: '', content: null });
    const [selectedFile, setSelectedFile] = useState(null);
    const [scheduleOpen, setScheduleOpen] = useState(null);
    const [scheduleTemp, _setScheduleTemp] = useState(Moment());
    const scheduleTempRef = useRef(scheduleTemp);
    const [openDeleteStaged, setOpenDeleteStaged] = useState(false);
    const [openDeleteDeployed, setOpenDeleteDeployed] = useState(false);
    const [openDeploy, setOpenDeploy] = useState(false);
    const [deleteMenu, setDeleteMenu] = useState(null);
    const [openUnschedule, setOpenUnschedule] = useState(false);
    const [openSaveSchedule, setOpenSaveSchedule] = useState(false);
    const upgradePerms = Auth.permissions.get('UpgradePolicy');
    const [readonly] = useState(!upgradePerms.write);
    const { height } = useWindowSize();
    const [mode, setMode] = useState('sensor_upgrade');
    const tableAPIRef = useApiRef();
    const [columnWidth, setColumnWidth] = useState({});
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [pushing, setPushing] = useState(false);

    const setScheduleTemp = (state) => {
        scheduleTempRef.current = state;
        _setScheduleTemp(state);
    };

    useEffect(() => {
        if (params.policy) {
            getUpgradeFiles();
            getUpgradeSchedule();
        }
        setColumnWidth(getProfile('sensor_upgrade').widths || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (uploadFile && uploadFile.name) {
            setPreStage({
                id: Helpers.UniqueID(),
                name: uploadFile.name,
                modified: new Date().toISOString(),
                status: 'Uploading',
                progress: 0,
                prestage: true,
            });
            handleUploadFile();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadFile]);

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('sensor_upgrade', {
            ...getProfile('sensor_upgrade'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const handleColumnReorder = () => {
        let order = [];
        setTimeout(() => {
            order = tableAPIRef.current.getAllColumns().reduce((a, c) => {
                a.push(c.headerName);
                return a;
            }, []);

            updateProfile('sensor_upgrade', {
                ...getProfile('sensor_upgrade'),
                columns: order,
            });
        });
    };

    const getUpgradeFiles = () => {
        UpgradeService.listUpgradeFiles('sensor', params.policy).then(res => {
            let u = [];

            if (res.stagedFiles) {
                const s = res.stagedFiles.map(v => ({
                    ...v,
                    progress: 100,
                    status: 'Complete',
                    id: Helpers.UniqueID()
                }));
                setStaged(s);
                u = _cloneDeep(s);
            } else {
                setStaged([]);
            }
            if (res.deployedFiles) {
                const d = res.deployedFiles.map(v => ({ ...v, id: Helpers.UniqueID() }));
                setDeployed(d);
                d.forEach((v) => {
                    if (u.filter(x => x.name === v.name).length) {
                        return;
                    }
                    u.push(v);
                });
            } else {
                setDeployed([]);
            }
            setUpgrades(u);
        }, err => {
            toast.error(err);
        });
    };

    const getUpgradeSchedule = () => {
        PolicyService.getPolicy('sensor_upgrade', params.policy).then(res => {
            setPolicy(res);
            setSchedule(res.data);
        });
        BacktestingService.getBTSensorUpgradeSchedule(params.policy).then(res => {
            setBTSchedule(res);
        }, err => {
            console.error(err);
        });
    };

    const handleGetFile = (e) => {
        setUploadFile(e.target.files[0]);
    };

    const handleUploadFile = () => {
        UpgradeService.uploadUpgradeFile('sensor', uploadFile).then(res => {
            toast.success('Install file uploaded successfully');
            getUpgradeFiles();
            getUpgradeSchedule();
            setPreStage([]);
            setSelectedFile(null);
            setUploadFile(null);
        }, err => {
            if (err) {
                if (err.message && err.error) {
                    toast.error(`${err.message}:  ${err.error}`);
                } else if (err.message) {
                    toast.error(err.message);
                } else if (err.error) {
                    toast.error(err.error);
                } else {
                    toast.error(err);
                }
            }
            setPreStage([]);
            setUploadFile(null);
        });
    };

    const tableRenderers = {
        fileName: (d) => (<div>{d.row.name}</div>),
        updated: (d) => (<div>{Moment(d.row.modified).format(theme.date.long)}</div>),
        scheduled: (d) => {
            if (_get(schedule, 'upgrade_confs.upgrade_schedule.filename', '') === d.row.name) {
                return <div>{Moment(schedule.upgrade_confs.upgrade_schedule.datetimets).format(theme.date.long)}</div>;
            } else if (_get(btSchedule, 'upgrade_confs.upgrade_schedule.filename', '') === d.row.name) {
                return <div>{Moment(btSchedule.upgrade_confs.upgrade_schedule.datetimets).format(theme.date.long)}</div>;
            }
            return <div>Not scheduled</div>;
        },
        progress: (d) => {
            return (
                <Box className={classes.progressContainer}>
                    <Box className={classes.statusMessage}>{d.row.status || 'Complete'}</Box>
                    <LinearProgress
                        variant={d.row.status !== 'Uploading' ? 'determinate' : 'indeterminate'}
                        value={100}
                        classes={{
                            barColorPrimary: d.row.status !== 'Uploading' ? (d.row.status === 'Failed' ? classes.colorPrimaryError : classes.colorPrimaryDone) : classes.colorPrimaryUploading,
                            determinate: classes.progressHeight,
                            indeterminate: classes.progressHeight,
                        }}
                    />
                </Box>
            );
        },
        deployed: (d) => {
            const i = deployed.findIndex(v => (v.name === d.row.name && !d.row.prestage));
            return (
                <Box>
                    {i !== -1 ? Moment(deployed[i].modified).format(theme.date.long) : 'Not Deployed'}
                </Box>
            );
        },
    };

    const handleDeleteStagedFile = () => {
        handleToggleDeleteStaged();
        UpgradeService.deleteUpgradeFile('sensor', 'Staged', selectedFile.name, params.policy).then(() => {
            toast.success('Staged file deleted successfully');
            if (selectedFile.name === _get(schedule, 'upgrade_confs.upgrade_schedule.filename')) {
                setSchedule({ upgrade_confs: { upgrade_schedule: {} } });
            } else if (selectedFile.name === _get(btSchedule, 'upgrade_confs.upgrade_schedule.filename')) {
                setBTSchedule({ upgrade_confs: { upgrade_schedule: {} } });
            }
            getUpgradeFiles();
            setPreStage([]);
            setSelectedFile(null);
        });
    };

    const handleDeleteDeployedFile = () => {
        handleToggleDeleteDeployed();
        UpgradeService.deleteUpgradeFile('sensor', 'Deployed', selectedFile.name, params.policy).then(() => {
            toast.success('Deployed file deleted successfully', {
                onOpen: () => {
                    getUpgradeFiles();
                    getUpgradeSchedule();
                    setSelectedFile(null);
                },
            });
            getUpgradeFiles();
        });
    };

    const handleDeployStagedFile = () => {
        handleToggleDeploy();
        setPushing(true);
        console.log('Deploying sensor upgrade file', selectedFile.name);
        UpgradeService.deployUpgradeFile('sensor', selectedFile.name, params.policy).then(res => {
            console.log('success', res);
            toast.success('Deploying sensor upgrade file...');
            setTimeout(() => {
                getUpgradeFiles();
            }, 100);
        }, err => {
            console.log(err);
            toast.error(`Error deploying upgrade, ${err.message}`);
        }).finally(() => { setPushing(false) });
    };

    const handleScheduleStagedFile = (e) => {
        setScheduleTemp(Moment().add(10, 'minutes'));
        setScheduleOpen(e.currentTarget);
    };

    const handleCloseSchedulePopup = () => {
        setScheduleOpen(null);
    };

    const handleUnscheduleStagedFile = () => {
        handleToggleUnschedule();
        // audit noise reduction
        const filename = _get(schedule, 'upgrade_confs.upgrade_schedule.filename');
        const s = _set(schedule, 'upgrade_confs.upgrade_schedule', { filename });
        setScheduleTemp(Moment().add(10, 'minutes'));
        if (selectedFile.name.includes('btsensor')) {
            setBTSchedule(s);
            handleSaveBTSchedule(s);
        } else {
            setSchedule(s);
            handleSaveSchedule(s);
        }
    };

    const handleSetSelected = (s) => {
        setSelectedFile(s.row);
    };

    const handleUpdateSchedule = (a) => {
        setScheduleTemp(a);
    };

    const handleCancelScheduleUpdate = () => {
        handleCloseSchedulePopup();
    };

    const handleSaveScheduleUpdate = () => {
        setOpenSaveSchedule(false);
        if (Moment(scheduleTemp).isBefore(Moment())) {
            toast.error('Error updating upgrade schedule: Cannot schedule upgrade in the past');
            return;
        }

        getUpgradeFiles();
        handleCloseSchedulePopup();

        const s = {
            upgrade_confs: {
                upgrade_schedule: {
                    datetimets: scheduleTemp.toISOString(),
                    filename: selectedFile.name
                },
            },
        };

        if (selectedFile.name.includes('btsensor')) {
            setBTSchedule(s);
            handleSaveBTSchedule(s);
        } else {
            setSchedule(s);
            handleSaveSchedule(s);
        }
    };

    const handleToggleDeleteStaged = () => {
        setOpenDeleteStaged(!openDeleteStaged);
    };

    const handleToggleDeleteDeployed = () => {
        setOpenDeleteDeployed(!openDeleteDeployed);
    };

    const handleToggleDeploy = () => {
        setOpenDeploy(!openDeploy);
    };

    const handleToggleUnschedule = () => {
        setOpenUnschedule(!openUnschedule);
    };

    const handleToggleSchedule = () => {
        setOpenSaveSchedule(!openSaveSchedule);
    };

    const handleSaveSchedule = (s) => {
        PolicyService.updatePolicy('sensor_upgrade', params.policy, {
            ...policy,
            data: s,
        }).then(() => {
            toast.success('The schedule saved successfully');
            getUpgradeFiles();
            getUpgradeSchedule();
        }, err => {
            toast.error(err);
        });
    };

    const handleSaveBTSchedule = (s) => {
        BacktestingService.updateBTSensorUpgradeSchedule(params.policy, s).then(res => {
            toast.success('The schedule saved successfully');
        }, err => {
            console.error(err);
            toast.error('Unable to update Backtesting sensor schedule');
        });
    };

    const handlePush = () => {
        setMode('push');
    };

    const handleClosePush = () => {
        setMode('sensor_upgrade');
    };

    const handleDeployPolicy = (sensorList) => {
        PolicyService.updatePolicy('sensor_upgrade', params.policy, policy).then(res => {
            toast.success('Policy saved successfully');
            getUpgradeSchedule();
            setMode('sensor_upgrade');

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('sensor_upgrade', params.policy, sensorList).then(res => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        });
    };

    const handleToggleDeleteMenu = (e) => {
        setDeleteMenu(e.currentTarget);
    };

    const handleCloseDeleteMenu = () => {
        setDeleteMenu(null);
    };

    return (
        <Grid container spacing={2}>
            {/* <Grid item xs={7}>
                <Box className={classes.infoPanel}>
                    Sensors and Backtesting sensors can be assigned the same policy. A sensor or backtesting sensor (btsensor)
                    will only be upgraded if an <code>install_sensor</code> or <code>install_btsensor</code> package has been uploaded respectively.
                </Box>
            </Grid> */}
            {!readonly && <Grid item xs={12} container justifyContent="flex-end">
                <Box display="flex" justifyContent="flex-end">
                    <Tooltip title={UPGRADE_INFORMATION}>
                        <span>
                            <IconButton><InfoIcon /></IconButton>
                        </span>
                    </Tooltip>
                    <input
                        id="sensor-upgrade-file"
                        style={{ display: 'none' }}
                        type="file"
                        onClick={(e) => { e.target.value = null }}
                        onChange={handleGetFile}
                    />
                    <label htmlFor="sensor-upgrade-file">
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            aria-label="upload"
                        ><AddIcon /> Upload</Button>
                    </label>
                    {upgradePerms.admin && <Box ml={2}><Button
                        variant="contained"
                        color="primary"
                        aria-label="push"
                        onClick={handlePush}
                        disabled={mode === 'push'}
                    >Push</Button></Box>}
                </Box>
            </Grid>}
            <Grid container item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                {upgradePerms.admin && <Push
                    policyName={params.policy}
                    policyType="sensor_upgrade"
                    onDeploy={handleDeployPolicy}
                    closePush={handleClosePush}
                    pushing={pushing}
                    permName={'UpgradePolicy'}
                />}
            </Grid>
            <Grid container item xs={12} style={{ display: mode === 'sensor_upgrade' ? '' : 'none' }}>
                <Box width="100%" style={{ height: Math.max(height - 310, 500) }}>
                    {!readonly && <Box className={classes.tableHeaderActions} p={1} display="flex" justifyContent="flex-end">
                        <Box ml={2}>
                            <Tooltip title="Delete">
                                <span>
                                    <IconButton
                                        disabled={!selectedFile}
                                        color="secondary"
                                        aria-label="delete"
                                        onClick={handleToggleDeleteMenu}
                                        size="large">
                                        <DeleteIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Menu open={Boolean(deleteMenu)} anchorEl={deleteMenu} onClose={handleCloseDeleteMenu}>
                                <Box display={selectedFile ? (staged.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                    <MenuItem onClick={handleToggleDeleteStaged}>Delete Staged</MenuItem>
                                </Box>
                                <Box display={selectedFile ? (deployed.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                    <MenuItem onClick={handleToggleDeleteDeployed}>Delete Deployed</MenuItem>
                                </Box>
                            </Menu>
                        </Box>
                        <Box ml={2}>
                            <Tooltip title="Cancel Schedule">
                                <span>
                                    <IconButton
                                        disabled={(() => {
                                            if (!selectedFile) return true;
                                            if (selectedFile.name.includes('btsensor')) {
                                                return !(_get(btSchedule, 'upgrade_confs.upgrade_schedule.filename') === selectedFile.name);
                                            }
                                            return !(_get(schedule, 'upgrade_confs.upgrade_schedule.filename') === selectedFile.name);
                                        })()}
                                        color="secondary"
                                        aria-label="Unschedule"
                                        onClick={handleToggleUnschedule}
                                        size="large">
                                        <UnscheduleIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <Box ml={2}>
                            <Tooltip title="Schedule">
                                <span>
                                    <IconButton
                                        color="primary"
                                        aria-label="schedule"
                                        onClick={handleScheduleStagedFile}
                                        disabled={(() => {
                                            if (selectedFile) {
                                                if (!selectedFile.upgradable) {
                                                    return true;
                                                }
                                                if (!deployed.findIndex(v => (v.name === selectedFile.name && !selectedFile.prestage))) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        })()}
                                        size="large">
                                        <ScheduleIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Box className={classes.hideBox}>
                                <DateTimePicker
                                    onChange={handleUpdateSchedule}
                                    value={scheduleTemp}
                                    disablePast
                                    open={!!scheduleOpen}
                                    onClose={handleCancelScheduleUpdate}
                                    onAccept={handleToggleSchedule}
                                    inputFormat={theme.date.short}
                                    views={['day', 'hours', 'minutes']}
                                />
                            </Box>
                        </Box>
                        <Box ml={2}>
                            <Tooltip title="Deploy">
                                <Box mt={1}>
                                    <Button
                                        color="primary"
                                        aria-label="deploy"
                                        onClick={handleToggleDeploy}
                                        disabled={(() => {
                                            if (selectedFile) {
                                                if (!selectedFile.upgradable) {
                                                    return true;
                                                }
                                                if (!deployed.findIndex(v => (v.name === selectedFile.name && !selectedFile.prestage))) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        })()}
                                    >
                                        Deploy
                                    </Button>
                                </Box>
                            </Tooltip>
                        </Box>
                    </Box>}
                    <DataGridPro
                        apiRef={tableAPIRef}
                        rows={Array.prototype.concat(preStage, upgrades)}
                        columns={(() => {
                            const p = getProfile('sensor_upgrade');
                            let c = [
                                { sortable: true, width: columnWidth['File Name'] || 0, ...(!columnWidth['File Name'] ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'File Name', field: 'file_name', renderCell: tableRenderers.fileName },
                                { sortable: true, width: columnWidth.Uploaded || 0, ...(!columnWidth.Uploaded ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Uploaded', field: 'create', renderCell: tableRenderers.updated },
                                { sortable: true, width: columnWidth.Scheduled || 0, ...(!columnWidth.Scheduled ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Scheduled', field: 'updated', renderCell: tableRenderers.scheduled },
                                { sortable: true, width: columnWidth['Upload Progress'] || 0, ...(!columnWidth['Upload Progress'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Upload Progress', field: 'progress', renderCell: tableRenderers.progress },
                                { sortable: true, width: columnWidth['Deployed At'] || 0, ...(!columnWidth['Deployed At'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Deployed At', field: 'deployed', renderCell: tableRenderers.deployed },
                            ];

                            if (p.columns && p.columns.length) {
                                c = p.columns.map(v => {
                                    for (let i = c.length; i--;) {
                                        if (v === c[i].headerName) {
                                            return c[i];
                                        }
                                    }
                                    return null;
                                });
                            }

                            return c;
                        })()}
                        disableMultipleColumnsSorting
                        disableMultipleSelection
                        disableColumnFilter
                        disableColumnMenu
                        onRowClick={handleSetSelected}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnReorder}
                    />
                </Box>
            </Grid>
            <ConfirmationDialog
                onClose={handleToggleDeleteStaged}
                onOK={handleDeleteStagedFile}
                open={openDeleteStaged}
                message={`Are you sure you want to delete the staged file ${selectedFile && selectedFile.name}?`}
                title="Delete staged file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeleteDeployed}
                onOK={handleDeleteDeployedFile}
                open={openDeleteDeployed}
                message={`Are you sure you want to delete the deployed file ${selectedFile && selectedFile.name}?`}
                title="Delete deployed file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeploy}
                onOK={handleDeployStagedFile}
                open={openDeploy}
                message={`Are you sure you want to deploy the file ${selectedFile && selectedFile.name} for upgrade?`}
                title="Deploy staged file for upgrade"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleUnschedule}
                onOK={handleUnscheduleStagedFile}
                open={openUnschedule}
                message={`Are you sure you want to unschedule ${selectedFile && selectedFile.name} for deployment?`}
                title="Unschedule"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleSchedule}
                onOK={handleSaveScheduleUpdate}
                open={openSaveSchedule}
                message={`Are you sure you want to schedule ${selectedFile && selectedFile.name} for deployment at ${scheduleTempRef.current.format(theme.date.long)}`}
                title="Schedule for deployment"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default SensorUpgrade;
